import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Download, FileText, DollarSign, Calendar, Building2, Eye, File, ExternalLink, Info, FileCheck, Mail, Phone, MapPin, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { LoadingState } from '@/components/ui/LoadingState';
import { Alert } from '@/components/ui/Alert/Alert';
import { VStack, HStack } from '@/components/ui/Stack';
import { Badge } from '@/components/ui/Badge/Badge';
import { Tabs, TabsList, TabsTrigger, TabsContent, Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui';
import { CventService } from '@/services/CventService';
import { ResearchService } from '@/services/ResearchService';
import { useNotificationStore } from '@/stores/notificationStore';
import { FileViewerModal, FileViewerFile } from '@/components/ui/FileViewer/FileViewerModal';
import { apiFetch } from '@/lib/api';

interface ProposalDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  rfpId: string;
  supplierId: string;
  venueName: string;
  shortlistId: string;
}

interface ProposalData {
  id?: string;
  rfpId?: string;
  name?: string;
  venueType?: string;
  address1?: string;
  address2?: string;
  stateProvinceCode?: string;
  countryCode?: string;
  postalCode?: string;
  venueMainPhone?: string;
  venueSalesPhone?: string;
  status?: string;
  responseStatus?: string;
  originalSendDate?: string;
  lastSendDate?: string;
  originalRfpSentDateTime?: string;
  lastRfpSentDateTime?: string;
  lastRfpViewedDateTime?: string;
  statusChangedDateTime?: string;
  lastModified?: string;
  awardedDate?: string;
  contact?: {
    firstName?: string;
    lastName?: string;
    organization?: string;
    organizationWebsite?: string;
    title?: string;
    email?: string;
    phone?: string;
    fax?: string;
    mobile?: string;
    additionalInfo?: string;
  };
  responseOverview?: {
    status?: string;
    originalResponseDate?: string;
    lastResponseDate?: string;
    proposalSentDateTime?: string;
    proposalLastSentDateTime?: string;
    responseComment?: string;
    responseContact?: {
      firstName?: string;
      lastName?: string;
      title?: string;
      email?: string;
      phone?: string;
    };
    isProposalInProgress?: boolean;
    awardedEventStartDate?: string;
    awardedEventEndDate?: string;
    venueProviderMappings?: Array<{
      providerTypeId?: number;
      providerCode?: string;
    }>;
    lowestSleepingRoomRate?: number;
    currencyCode?: string;
    responseStatus?: string;
  };
  proposal?: {
    proposalId?: string;
    externalProposalId?: string;
    currencyCode?: string;
    isCommissionableBid?: boolean;
    commissionRate?: number;
    proposalIntroduction?: string;
    billingContractualResponse?: string;
    taxAdditionalInfo?: string;
    eventAdditionalInfo?: string;
    isFnbCost?: boolean;
    foodBeverageMinimum?: number;
    foodBeverageMinimumNote?: string;
    applicableTaxPercent?: number;
    serviceChargePercent?: number;
    estimatedCostsAdditionalInfo?: string;
    unionInfo?: string;
    contractorInfo?: string;
    proposalCvbVisible?: boolean;
    proposalSent?: boolean;
    agendaStartDate?: string;
    sleepingRoomAvailability?: {
      additionalInfo?: string;
      sleepingRoomReqMetTypeId?: boolean;
      sleepingRoomReqMetType?: string;
    };
    meetingSpaceAvailability?: {
      additionalInfo?: string;
      avNeeds?: string;
      meetingRoomReqMetTypeId?: boolean;
    };
    eventAvailability?: {
      startDate?: string;
      endDate?: string;
      availability?: string;
      additionalInfo?: string;
      hold?: boolean;
      includePackagePricing?: boolean;
    };
    plannerProposalStatus?: string;
    proposalDerivedDetail?: {
      lowestRoomRate?: number;
      maxRoomRate?: number;
      totalSleepRoomCost?: number;
      totalFnbCost?: number;
      totalMiscCost?: number;
    };
    isBreakfastInclusive?: boolean;
    isTaxInclusive?: boolean;
    proposalVisibleToCvbs?: string[];
    commissions?: Array<{
      rate?: number;
      applicableType?: string;
      id?: string;
    }>;
    sleepingRooms?: Array<{
      dateType?: string;
      peakRoomNights?: number;
      totalRoomNights?: number;
      checkInDate?: string;
      additionalInfo?: string;
      budgetedRoomRate?: number;
      roomBlocks?: Array<{
        id?: string;
        date?: string;
        dayNumber?: number;
        totalRooms?: number;
        note?: string;
        roomDetails?: Array<{
          type?: string;
          quantity?: number;
          rate?: number;
        }>;
      }>;
      roomOccupancyPerRoomType?: Array<{
        type?: string;
        occupancy?: number;
      }>;
      isRoomRequirementsMet?: boolean;
      responseNote?: string;
      lowestRate?: number;
      maximumRate?: number;
      lowestAverageDailyRate?: number;
      maximumAverageDailyRate?: number;
      totalRoomCost?: number;
    }>;
    baseProposal?: {
      estimatedCosts?: {
        sleepingRatesTaxInclusive?: boolean;
        sleepingRatesBreakfastInclusive?: boolean;
        applicableTaxPercent?: number;
        serviceChargePercent?: number;
        additionalFees?: Array<{
          type?: string;
          taxApplicableType?: string;
          feeCategory?: string;
          feeCategoryName?: string;
          feeType?: string;
          feeValue?: number | string;
        }>;
      };
    };
  };
  hasCustomProposalTemplate?: boolean;
  notesRecommendations?: string;
}

interface Attachment {
  id?: string;
  attachmentId?: string;
  name?: string;
  fileName?: string;
  fileMimeType?: string;
  documentType?: string;
  fileLength?: number;
  size?: number;
  contentUrl?: string;
  attachmentType?: string;
  uploadDate?: string;
}

export function ProposalDetailsModal({
  isOpen,
  onClose,
  rfpId,
  supplierId,
  venueName,
  shortlistId,
}: ProposalDetailsModalProps) {
  const [proposalData, setProposalData] = useState<ProposalData | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRawDataModal, setShowRawDataModal] = useState(false);
  const [rawDatabaseData, setRawDatabaseData] = useState<Record<string, any> | null>(null);
  const [loadingRawData, setLoadingRawData] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileViewerFile | null>(null);
  const [activeTab, setActiveTab] = useState('general');
  const [proposalSummary, setProposalSummary] = useState<string | null>(null);
  
  // Proposal PDF states
  const [proposalPdfUrl, setProposalPdfUrl] = useState<string | null>(null);
  const [proposalPdfFetchedAt, setProposalPdfFetchedAt] = useState<string | null>(null);
  const [fetchingProposalPdf, setFetchingProposalPdf] = useState(false);
  
  const cventService = new CventService();
  const researchService = new ResearchService();
  const notificationStore = useNotificationStore();

  useEffect(() => {
    if (isOpen && rfpId && supplierId) {
      loadProposalData();
      loadAttachments();
      loadProposalPdf();
      loadProposalSummary();
    } else {
      setProposalData(null);
      setAttachments([]);
      setError(null);
      setSelectedFile(null);
      setActiveTab('general');
      setProposalPdfUrl(null);
      setProposalPdfFetchedAt(null);
      setProposalSummary(null);
    }
  }, [isOpen, rfpId, supplierId, shortlistId]);

  async function loadProposalSummary() {
    try {
      const shortlistItem = await researchService.getShortlistItem(shortlistId);
      setProposalSummary(shortlistItem.cvent_proposal_summary || null);
    } catch (err) {
      console.error('Failed to load proposal summary:', err);
      // Don't set error state - summary is optional
    }
  }

  async function loadProposalData() {
    try {
      setLoading(true);
      setError(null);
      const data = await cventService.fetchSupplierProposal(rfpId, supplierId) as ProposalData;
      setProposalData(data);
      
      // Store full proposal data to database
      try {
        await apiFetch(`/api/shortlist/${shortlistId}/store-proposal-data`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ proposal_data: data }),
        });
      } catch (storeError) {
        // Log but don't fail - data is still displayed
        console.warn('Failed to store proposal data to database:', storeError);
      }
    } catch (err: any) {
      console.error('Failed to load proposal data:', err);
      setError(err?.message || 'Failed to load proposal data');
    } finally {
      setLoading(false);
    }
  }

  async function loadAttachments() {
    try {
      setLoadingAttachments(true);
      const result = await cventService.fetchSupplierAttachments(rfpId, supplierId) as any;
      const attachmentsList = result?.items || result || [];
      setAttachments(attachmentsList);
    } catch (err: any) {
      console.error('Failed to load attachments:', err);
    } finally {
      setLoadingAttachments(false);
    }
  }

  async function loadProposalPdf() {
    try {
      console.log('[loadProposalPdf] Loading PDF info for shortlist:', shortlistId);
      
      // Get shortlist item to check if PDF exists
      const shortlistItem = await researchService.getShortlistItem(shortlistId);
      console.log('[loadProposalPdf] Shortlist item:', {
        hasAttachmentId: !!shortlistItem.cvent_proposal_pdf_attachment_id,
        attachmentId: shortlistItem.cvent_proposal_pdf_attachment_id,
        fetchedAt: shortlistItem.cvent_proposal_pdf_fetched_at,
      });
      
      if (shortlistItem.cvent_proposal_pdf_attachment_id) {
        // PDF exists, get the attachment to retrieve the R2 URL
        const response = await apiFetch(`/api/attachments/${shortlistItem.cvent_proposal_pdf_attachment_id}`);
        
        if (response.ok) {
          const result = await response.json();
          const attachment = result.data;
          console.log('[loadProposalPdf] Attachment loaded:', {
            id: attachment.id,
            url: attachment.url,
          });
          // Use the direct R2 URL from the attachment
          setProposalPdfUrl(attachment.url);
          setProposalPdfFetchedAt(shortlistItem.cvent_proposal_pdf_fetched_at);
        } else {
          console.error('[loadProposalPdf] Failed to fetch attachment:', response.status, response.statusText);
        }
      }
    } catch (err) {
      console.error('[loadProposalPdf] Failed to load proposal PDF info:', err);
      // Not a critical error, just means no PDF available yet
    }
  }

  async function handleFetchProposalPdf(forceRefresh = false) {
    try {
      setFetchingProposalPdf(true);
      const result = await researchService.fetchProposalPdf(shortlistId, forceRefresh);
      
      // Update state with the direct R2 URL from the API response
      setProposalPdfUrl(result.url);
      setProposalPdfFetchedAt(result.fetchedAt);
      
      notificationStore.success(
        forceRefresh ? 'Proposal PDF refreshed successfully' : 'Proposal PDF fetched successfully'
      );
    } catch (err: any) {
      console.error('Failed to fetch proposal PDF:', err);
      
      let errorMessage = err?.message || 'Failed to fetch proposal PDF';
      let errorTitle = 'Fetch Error';
      
      // Check for specific error types
      if (errorMessage.includes('authentication') || errorMessage.includes('cookies')) {
        // Check if auto-refresh was attempted
        if (errorMessage.includes('Attempted to refresh authentication')) {
          errorMessage = 'Failed to fetch proposal PDF. Automatic cookie refresh was attempted but failed. Please check Cvent login credentials in Admin Settings → Cvent Settings.';
          errorTitle = 'Authentication Error';
        } else {
          errorMessage = 'Cvent authentication required. Attempting to refresh cookies automatically...';
        }
      } else if (errorMessage.includes('not connected')) {
        errorMessage = 'This venue is not connected to a Cvent RFP supplier.';
      } else if (errorMessage.includes('No PDF proposal available')) {
        errorMessage = 'The proposal PDF is not available yet. The hotel may not have submitted a proposal, or the PDF may not be accessible.';
      }
      
      notificationStore.error(errorMessage, {
        title: errorTitle,
      });
    } finally {
      setFetchingProposalPdf(false);
    }
  }

  async function handleViewRawData() {
    setShowRawDataModal(true);
    setLoadingRawData(true);
    try {
      // Fetch the shortlist item which contains all database fields
      const shortlistItem = await researchService.getShortlistItem(shortlistId);
      
      // Extract all Cvent-related fields from the database
      const dbFields: Record<string, any> = {};
      
      // Cvent connection fields
      if (shortlistItem.cvent_supplier_id) dbFields['cvent_supplier_id'] = shortlistItem.cvent_supplier_id;
      if (shortlistItem.cvent_match_confidence !== null) dbFields['cvent_match_confidence'] = shortlistItem.cvent_match_confidence;
      if (shortlistItem.cvent_match_signals) dbFields['cvent_match_signals'] = shortlistItem.cvent_match_signals;
      if (shortlistItem.cvent_connected_at) dbFields['cvent_connected_at'] = shortlistItem.cvent_connected_at;
      
      // Cvent proposal data fields
      if (shortlistItem.cvent_proposal_status) dbFields['cvent_proposal_status'] = shortlistItem.cvent_proposal_status;
      if (shortlistItem.cvent_lowest_room_rate !== null) dbFields['cvent_lowest_room_rate'] = shortlistItem.cvent_lowest_room_rate;
      if (shortlistItem.cvent_max_room_rate !== null) dbFields['cvent_max_room_rate'] = shortlistItem.cvent_max_room_rate;
      if (shortlistItem.cvent_total_cost !== null) dbFields['cvent_total_cost'] = shortlistItem.cvent_total_cost;
      if (shortlistItem.cvent_total_sleeping_room_cost !== null) dbFields['cvent_total_sleeping_room_cost'] = shortlistItem.cvent_total_sleeping_room_cost;
      if (shortlistItem.cvent_total_fnb_cost !== null) dbFields['cvent_total_fnb_cost'] = shortlistItem.cvent_total_fnb_cost;
      if (shortlistItem.cvent_total_misc_cost !== null) dbFields['cvent_total_misc_cost'] = shortlistItem.cvent_total_misc_cost;
      if (shortlistItem.cvent_currency_code) dbFields['cvent_currency_code'] = shortlistItem.cvent_currency_code;
      if (shortlistItem.cvent_original_response_date) dbFields['cvent_original_response_date'] = shortlistItem.cvent_original_response_date;
      if (shortlistItem.cvent_last_response_date) dbFields['cvent_last_response_date'] = shortlistItem.cvent_last_response_date;
      if (shortlistItem.cvent_proposal_data_refreshed_at) dbFields['cvent_proposal_data_refreshed_at'] = shortlistItem.cvent_proposal_data_refreshed_at;
      if (shortlistItem.cvent_applicable_tax_percent !== null) dbFields['cvent_applicable_tax_percent'] = shortlistItem.cvent_applicable_tax_percent;
      if (shortlistItem.cvent_service_charge_percent !== null) dbFields['cvent_service_charge_percent'] = shortlistItem.cvent_service_charge_percent;
      if (shortlistItem.cvent_commission_rate !== null) dbFields['cvent_commission_rate'] = shortlistItem.cvent_commission_rate;
      if (shortlistItem.cvent_food_beverage_minimum !== null) dbFields['cvent_food_beverage_minimum'] = shortlistItem.cvent_food_beverage_minimum;
      if (shortlistItem.cvent_is_tax_inclusive !== null) dbFields['cvent_is_tax_inclusive'] = shortlistItem.cvent_is_tax_inclusive;
      if (shortlistItem.cvent_is_breakfast_inclusive !== null) dbFields['cvent_is_breakfast_inclusive'] = shortlistItem.cvent_is_breakfast_inclusive;
      if (shortlistItem.cvent_proposal_full_data_stored_at) dbFields['cvent_proposal_full_data_stored_at'] = shortlistItem.cvent_proposal_full_data_stored_at;
      
      // PDF fields
      if (shortlistItem.cvent_proposal_pdf_attachment_id) dbFields['cvent_proposal_pdf_attachment_id'] = shortlistItem.cvent_proposal_pdf_attachment_id;
      if (shortlistItem.cvent_proposal_pdf_fetched_at) dbFields['cvent_proposal_pdf_fetched_at'] = shortlistItem.cvent_proposal_pdf_fetched_at;
      
      // Full JSONB data (if exists)
      if (shortlistItem.cvent_proposal_data_jsonb) {
        dbFields['cvent_proposal_data_jsonb'] = shortlistItem.cvent_proposal_data_jsonb;
      }
      
      setRawDatabaseData(dbFields);
    } catch (err: any) {
      console.error('Failed to load raw database data:', err);
      notificationStore.error('Failed to load database data', {
        title: 'Error',
      });
    } finally {
      setLoadingRawData(false);
    }
  }

  function handleViewProposalPdf() {
    if (!proposalPdfUrl) return;
    
    setSelectedFile({
      url: proposalPdfUrl,
      name: `${venueName} Proposal.pdf`,
      mimeType: 'application/pdf',
    });
  }

  function handleViewAttachment(attachment: Attachment) {
    const fileUrl = attachment.contentUrl || 
      (attachment.id ? `/api/cvent/rfps/${rfpId}/suppliers/${supplierId}/attachment/${attachment.id}` : null);
    
    if (!fileUrl) {
      notificationStore.error('Unable to view attachment: No URL available');
      return;
    }

    setSelectedFile({
      url: fileUrl,
      name: attachment.name || attachment.fileName || 'Attachment',
      mimeType: attachment.fileMimeType || attachment.documentType,
      size: attachment.fileLength || attachment.size,
    });
  }

  function getFileIcon(mimeType?: string, documentType?: string) {
    const type = (mimeType || documentType || '').toLowerCase();
    if (type.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf' || type === 'pdf') return '📄';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    if (type === 'link') return '🔗';
    return '📎';
  }

  async function handleDownloadProposal() {
    try {
      setDownloading(true);
      const blob = await cventService.downloadProposalPdf(rfpId, supplierId);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proposal-${venueName.replace(/[^a-z0-9]/gi, '_')}-${rfpId}-${supplierId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      notificationStore.success('Proposal downloaded successfully');
    } catch (err: any) {
      console.error('Failed to download proposal:', err);
      
      let errorMessage = err?.message || 'Failed to download proposal';
      
      if (errorMessage.includes('No PDF proposal available')) {
        errorMessage = 'The proposal PDF is not available yet. It may not have been submitted, or the PDF may not be accessible through the API. Please check the proposal in Cvent directly.';
      } else if (errorMessage.includes('No PDF')) {
        errorMessage = 'No PDF proposal found. The proposal may still be in progress or the PDF may not be available yet.';
      }
      
      notificationStore.error(errorMessage, {
        title: 'Download Error',
      });
    } finally {
      setDownloading(false);
    }
  }

  function getStatusBadge(status?: string) {
    if (!status) return null;
    
    const statusLower = status.toLowerCase();
    let variant: 'success' | 'warning' | 'error' | 'secondary' = 'secondary';
    
    if (statusLower.includes('received') || statusLower.includes('submitted') || statusLower === 'accepted' || statusLower === 'awarded') {
      variant = 'success';
    } else if (statusLower.includes('pending') || statusLower.includes('draft') || statusLower.includes('progress')) {
      variant = 'warning';
    } else if (statusLower.includes('declined') || statusLower === 'rejected' || statusLower.includes('turned down') || statusLower === 'turned_down') {
      variant = 'error';
    }
    
    return (
      <Badge variant={variant} className="text-xs">
        {status}
      </Badge>
    );
  }

  function formatCurrency(amount: number | undefined, currencyCode: string = 'USD'): string {
    if (amount === undefined || amount === null) return '-';
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${currencyCode || 'USD'} ${amount.toLocaleString()}`;
    }
  }

  function formatDate(dateString?: string): string {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  function formatDateTime(dateString?: string): string {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  }

  function calculateTotalCost(data: ProposalData): number | null {
    if (data.proposal?.proposalDerivedDetail) {
      const derived = data.proposal.proposalDerivedDetail;
      const sum = (derived.totalSleepRoomCost || 0) +
                 (derived.totalFnbCost || 0) +
                 (derived.totalMiscCost || 0);
      return sum > 0 ? sum : null;
    }
    return null;
  }

  const status = proposalData?.responseOverview?.status || proposalData?.status || proposalData?.responseStatus;
  const currencyCode = proposalData?.proposal?.currencyCode || proposalData?.responseOverview?.currencyCode || 'USD';
  const lowestRoomRate = proposalData?.proposal?.proposalDerivedDetail?.lowestRoomRate ||
                        proposalData?.responseOverview?.lowestSleepingRoomRate;
  const totalCost = calculateTotalCost(proposalData || {});

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Proposal Details"
      description={venueName}
      size="xl"
      footer={
        <HStack gap={2} justify="end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </HStack>
      }
    >
      <VStack gap={4}>
        {loading && (
          <div className="py-8">
            <LoadingState text="Loading proposal data..." />
          </div>
        )}

        {error && (
          <Alert variant="error">
            <p className="text-sm">{error}</p>
          </Alert>
        )}

        {!loading && !error && proposalData && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-2 mb-6">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <Info className="w-4 h-4" />
                <span>General Info</span>
              </TabsTrigger>
              <TabsTrigger value="proposal" className="flex items-center gap-2">
                <FileCheck className="w-4 h-4" />
                <span>Proposal</span>
              </TabsTrigger>
            </TabsList>

            {/* General Info Tab */}
            <TabsContent value="general" className="space-y-4">
            {/* Proposal Document - Only show if proposal is submitted */}
            {status === 'SUBMITTED_PROPOSAL' && (
              <Card>
                <CardHeader>
                  <CardTitle>Proposal Document</CardTitle>
                  <CardDescription>
                    {proposalPdfFetchedAt 
                      ? `Last fetched: ${formatDateTime(proposalPdfFetchedAt)}` 
                      : 'Cvent proposal PDF'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {proposalPdfUrl ? (
                    <HStack gap={2}>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleViewProposalPdf}
                        leftIcon={<Eye className="w-4 h-4" />}
                      >
                        View PDF
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleFetchProposalPdf(true)}
                        disabled={fetchingProposalPdf}
                        leftIcon={fetchingProposalPdf ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      >
                        {fetchingProposalPdf ? 'Refreshing...' : 'Refresh Proposal'}
                      </Button>
                    </HStack>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleFetchProposalPdf(false)}
                      disabled={fetchingProposalPdf}
                      leftIcon={fetchingProposalPdf ? <RefreshCw className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                    >
                      {fetchingProposalPdf ? 'Fetching...' : 'Fetch Proposal from Cvent'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Status */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Status:</span>
              {getStatusBadge(status)}
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {lowestRoomRate !== undefined && lowestRoomRate !== null && (
                  <Card>
                    <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Lowest Room Rate</span>
                  </div>
                  <div className="text-lg font-semibold text-foreground">
                    {formatCurrency(lowestRoomRate, currencyCode)}
                  </div>
                    </CardContent>
                  </Card>
              )}

              {totalCost !== null && (
                  <Card>
                    <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Total Cost</span>
                  </div>
                  <div className="text-lg font-semibold text-foreground">
                    {formatCurrency(totalCost, currencyCode)}
                  </div>
                    </CardContent>
                  </Card>
              )}
            </div>

            {/* Response Dates */}
            {(proposalData.responseOverview?.originalResponseDate || proposalData.responseOverview?.lastResponseDate) && (
                <Card>
                  <CardHeader>
                    <CardTitle>Response Dates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                  {proposalData.responseOverview.originalResponseDate && (
                    <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Original Response:</span>
                          <span className="font-medium">{formatDate(proposalData.responseOverview.originalResponseDate)}</span>
                    </div>
                  )}
                  {proposalData.responseOverview.lastResponseDate && (
                    <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Last Updated:</span>
                          <span className="font-medium">{formatDate(proposalData.responseOverview.lastResponseDate)}</span>
                    </div>
                  )}
                </div>
                  </CardContent>
                </Card>
              )}

              {/* Cost Breakdown */}
              {proposalData.proposal?.proposalDerivedDetail && (
                <Card>
                  <CardHeader>
                    <CardTitle>Cost Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                <div className="space-y-2 text-sm">
                      {proposalData.proposal.proposalDerivedDetail.totalSleepRoomCost !== undefined && (
                    <div className="flex justify-between py-1 border-b border-gray-200">
                      <span className="text-muted-foreground">Sleeping Rooms</span>
                          <span className="font-medium">{formatCurrency(proposalData.proposal.proposalDerivedDetail.totalSleepRoomCost, currencyCode)}</span>
                    </div>
                  )}
                      {proposalData.proposal.proposalDerivedDetail.totalFnbCost !== undefined && (
                    <div className="flex justify-between py-1 border-b border-gray-200">
                      <span className="text-muted-foreground">Food & Beverage</span>
                          <span className="font-medium">{formatCurrency(proposalData.proposal.proposalDerivedDetail.totalFnbCost, currencyCode)}</span>
                    </div>
                  )}
                      {proposalData.proposal.proposalDerivedDetail.totalMiscCost !== undefined && (
                    <div className="flex justify-between py-1 border-b border-gray-200">
                      <span className="text-muted-foreground">Miscellaneous</span>
                          <span className="font-medium">{formatCurrency(proposalData.proposal.proposalDerivedDetail.totalMiscCost, currencyCode)}</span>
                    </div>
                  )}
                    </div>
                  </CardContent>
                </Card>
            )}

            {/* Attachments Section */}
              <Card>
                <CardHeader>
                  <CardTitle>Attachments</CardTitle>
                  <CardDescription>Files and documents attached to this proposal</CardDescription>
                </CardHeader>
                <CardContent>
              {loadingAttachments ? (
                <div className="py-4">
                  <LoadingState text="Loading attachments..." size="sm" />
                </div>
              ) : attachments.length > 0 ? (
                <div className="space-y-2">
                  {attachments.map((attachment, index) => {
                    const attachmentId = attachment.id || attachment.attachmentId;
                    const attachmentName = attachment.name || attachment.fileName || `Attachment ${index + 1}`;
                    const attachmentType = attachment.fileMimeType || attachment.documentType || 'Unknown';
                    const attachmentSize = attachment.fileLength || attachment.size;
                    const isLink = attachmentType === 'LINK' || attachment.attachmentType === 'LINK';

                    return (
                      <div
                        key={attachmentId || index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="text-xl flex-shrink-0">
                            {getFileIcon(attachment.fileMimeType, attachment.documentType)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {attachmentName}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-muted-foreground">{attachmentType}</span>
                              {attachmentSize && (
                                <>
                                  <span className="text-xs text-muted-foreground">•</span>
                                  <span className="text-xs text-muted-foreground">
                                    {(attachmentSize / 1024).toFixed(1)} KB
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isLink && attachment.contentUrl ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(attachment.contentUrl, '_blank', 'noopener,noreferrer')}
                              leftIcon={<ExternalLink className="w-3.5 h-3.5" />}
                              className="text-xs"
                            >
                              Open Link
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewAttachment(attachment)}
                              leftIcon={<Eye className="w-3.5 h-3.5" />}
                              className="text-xs"
                            >
                              View
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-4 text-center text-muted-foreground">
                  <File className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No attachments available</p>
                </div>
              )}
                </CardContent>
              </Card>

              {/* View Raw Database Data Button - At bottom of General Info tab */}
              <Card>
                <CardContent className="pt-6">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleViewRawData}
                    leftIcon={<FileText className="w-4 h-4" />}
                  >
                    View Raw Database Data
                  </Button>
                  <p className="text-xs text-muted-foreground mt-2">
                    View all Cvent-related fields stored in the database for this venue
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Proposal Tab - Comprehensive Data */}
            <TabsContent value="proposal" className="space-y-4">
              {proposalData.proposal ? (
                <>
                  {/* Proposal Overview */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Proposal Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Proposal ID:</span>
                          <span className="ml-2 font-mono text-xs">{proposalData.proposal.proposalId || '-'}</span>
            </div>
                        <div>
                          <span className="text-muted-foreground">Currency:</span>
                          <span className="ml-2 font-medium">{currencyCode}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Commission:</span>
                          <span className="ml-2 font-medium">
                            {proposalData.proposal.isCommissionableBid ? `${proposalData.proposal.commissionRate || 0}%` : 'No'}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Proposal Sent:</span>
                          <span className="ml-2 font-medium">
                            {proposalData.proposal.proposalSent ? (
                              <span className="flex items-center gap-1 text-success-600">
                                <CheckCircle className="w-4 h-4" />
                                Yes
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-muted-foreground">
                                <XCircle className="w-4 h-4" />
                                No
                              </span>
                            )}
                          </span>
                        </div>
                        {proposalData.proposal.agendaStartDate && (
                          <div>
                            <span className="text-muted-foreground">Agenda Start Date:</span>
                            <span className="ml-2 font-medium">{formatDate(proposalData.proposal.agendaStartDate)}</span>
                          </div>
                        )}
                        {proposalData.proposal.plannerProposalStatus && (
                          <div>
                            <span className="text-muted-foreground">Status:</span>
                            <span className="ml-2 font-medium">{proposalData.proposal.plannerProposalStatus}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Room Breakdown - REST API Structure */}
                  {(proposalData.proposal.sleepingRoomAvailability || 
                    proposalData.proposal.isBreakfastInclusive !== undefined || 
                    proposalData.proposal.isTaxInclusive !== undefined ||
                    proposalData.proposal.taxAdditionalInfo ||
                    proposalData.proposal.proposalDerivedDetail) && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Sleeping Room Breakdown</CardTitle>
                        <CardDescription>
                          Room rates, availability, and cost information
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Summary Info */}
                          {proposalData.proposal.proposalDerivedDetail && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                              {proposalData.proposal.proposalDerivedDetail.lowestRoomRate !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Lowest Room Rate:</span>
                                  <span className="ml-2 font-medium">
                                    {formatCurrency(proposalData.proposal.proposalDerivedDetail.lowestRoomRate, currencyCode)}
                                  </span>
                                </div>
                              )}
                              {proposalData.proposal.proposalDerivedDetail.maxRoomRate !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Maximum Room Rate:</span>
                                  <span className="ml-2 font-medium">
                                    {formatCurrency(proposalData.proposal.proposalDerivedDetail.maxRoomRate, currencyCode)}
                                  </span>
                                </div>
                              )}
                              {proposalData.proposal.proposalDerivedDetail.totalSleepRoomCost !== undefined && (
                                <div>
                                  <span className="text-muted-foreground">Total Room Cost:</span>
                                  <span className="ml-2 font-medium">
                                    {formatCurrency(proposalData.proposal.proposalDerivedDetail.totalSleepRoomCost, currencyCode)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Tax and Breakfast Info */}
                          <div className="flex flex-wrap gap-4 text-sm">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">Tax Included:</span>
                              {proposalData.proposal.isTaxInclusive ? (
                                <span className="flex items-center gap-1 text-success-600 font-medium">
                                  <CheckCircle className="w-4 h-4" />
                                  Yes
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-muted-foreground font-medium">
                                  <XCircle className="w-4 h-4" />
                                  No
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">Breakfast Included:</span>
                              {proposalData.proposal.isBreakfastInclusive ? (
                                <span className="flex items-center gap-1 text-success-600 font-medium">
                                  <CheckCircle className="w-4 h-4" />
                                  Yes
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-muted-foreground font-medium">
                                  <XCircle className="w-4 h-4" />
                                  No
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Tax Information */}
                          {proposalData.proposal.taxAdditionalInfo && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">Tax Information:</h4>
                              <p className="text-sm text-foreground whitespace-pre-wrap">
                                {proposalData.proposal.taxAdditionalInfo}
                              </p>
                            </div>
                          )}

                          {/* Room Availability Details */}
                          {proposalData.proposal.sleepingRoomAvailability?.additionalInfo && (
                            <div>
                              <h4 className="text-sm font-medium mb-2">Room Availability & Rates:</h4>
                              <p className="text-sm text-foreground whitespace-pre-wrap">
                                {proposalData.proposal.sleepingRoomAvailability.additionalInfo}
                              </p>
                            </div>
                          )}

                          {/* Requirements Met Status */}
                          {proposalData.proposal.sleepingRoomAvailability?.sleepingRoomReqMetTypeId !== undefined && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">Room Requirements Met:</span>
                              {proposalData.proposal.sleepingRoomAvailability.sleepingRoomReqMetTypeId ? (
                                <span className="flex items-center gap-1 text-success-600 font-medium">
                                  <CheckCircle className="w-4 h-4" />
                                  Yes
                                  {proposalData.proposal.sleepingRoomAvailability.sleepingRoomReqMetType && (
                                    <span className="text-muted-foreground">
                                      ({proposalData.proposal.sleepingRoomAvailability.sleepingRoomReqMetType})
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-muted-foreground font-medium">
                                  <XCircle className="w-4 h-4" />
                                  No
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Pricing Details */}
                  {proposalData.proposal.proposalDerivedDetail && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Pricing Details</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {proposalData.proposal.proposalDerivedDetail.lowestRoomRate !== undefined && (
                            <div>
                              <span className="text-muted-foreground">Lowest Room Rate:</span>
                              <span className="ml-2 font-medium">{formatCurrency(proposalData.proposal.proposalDerivedDetail.lowestRoomRate, currencyCode)}</span>
                            </div>
                          )}
                          {proposalData.proposal.proposalDerivedDetail.maxRoomRate !== undefined && (
                            <div>
                              <span className="text-muted-foreground">Maximum Room Rate:</span>
                              <span className="ml-2 font-medium">{formatCurrency(proposalData.proposal.proposalDerivedDetail.maxRoomRate, currencyCode)}</span>
                            </div>
                          )}
                          {proposalData.proposal.proposalDerivedDetail.totalSleepRoomCost !== undefined && (
                            <div>
                              <span className="text-muted-foreground">Total Sleeping Room Cost:</span>
                              <span className="ml-2 font-medium">{formatCurrency(proposalData.proposal.proposalDerivedDetail.totalSleepRoomCost, currencyCode)}</span>
                            </div>
                          )}
                          {proposalData.proposal.proposalDerivedDetail.totalFnbCost !== undefined && (
                            <div>
                              <span className="text-muted-foreground">Total Food & Beverage Cost:</span>
                              <span className="ml-2 font-medium">{formatCurrency(proposalData.proposal.proposalDerivedDetail.totalFnbCost, currencyCode)}</span>
                            </div>
                          )}
                          {proposalData.proposal.proposalDerivedDetail.totalMiscCost !== undefined && (
                            <div>
                              <span className="text-muted-foreground">Total Miscellaneous Cost:</span>
                              <span className="ml-2 font-medium">{formatCurrency(proposalData.proposal.proposalDerivedDetail.totalMiscCost, currencyCode)}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Tax & Service Charges */}
                  {(proposalData.proposal.applicableTaxPercent !== undefined || proposalData.proposal.serviceChargePercent !== undefined) && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Tax & Service Charges</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {proposalData.proposal.applicableTaxPercent !== undefined && (
                            <div>
                              <span className="text-muted-foreground">Applicable Tax:</span>
                              <span className="ml-2 font-medium">{proposalData.proposal.applicableTaxPercent}%</span>
                            </div>
                          )}
                          {proposalData.proposal.serviceChargePercent !== undefined && (
                            <div>
                              <span className="text-muted-foreground">Service Charge:</span>
                              <span className="ml-2 font-medium">{proposalData.proposal.serviceChargePercent}%</span>
                            </div>
                          )}
                          <div>
                            <span className="text-muted-foreground">Breakfast Included:</span>
                            <span className="ml-2 font-medium">
                              {proposalData.proposal.isBreakfastInclusive ? (
                                <span className="flex items-center gap-1 text-success-600">
                                  <CheckCircle className="w-4 h-4" />
                                  Yes
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <XCircle className="w-4 h-4" />
                                  No
                                </span>
                              )}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Tax Included:</span>
                            <span className="ml-2 font-medium">
                              {proposalData.proposal.isTaxInclusive ? (
                                <span className="flex items-center gap-1 text-success-600">
                                  <CheckCircle className="w-4 h-4" />
                                  Yes
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-muted-foreground">
                                  <XCircle className="w-4 h-4" />
                                  No
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Food & Beverage Minimum */}
                  {proposalData.proposal.foodBeverageMinimum !== undefined && proposalData.proposal.foodBeverageMinimum > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Food & Beverage Minimum</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm">
                          <span className="text-muted-foreground">Minimum:</span>
                          <span className="ml-2 font-medium">{formatCurrency(proposalData.proposal.foodBeverageMinimum, currencyCode)}</span>
                          {proposalData.proposal.foodBeverageMinimumNote && (
                            <p className="mt-2 text-sm text-muted-foreground">{proposalData.proposal.foodBeverageMinimumNote}</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Commissions */}
                  {proposalData.proposal.commissions && proposalData.proposal.commissions.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Commissions</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {proposalData.proposal.commissions.map((commission, idx) => (
                            <div key={commission.id || idx} className="text-sm">
                              <span className="text-muted-foreground">Rate:</span>
                              <span className="ml-2 font-medium">{commission.rate}%</span>
                              {commission.applicableType && (
                                <>
                                  <span className="ml-2 text-muted-foreground">on</span>
                                  <span className="ml-2 font-medium">{commission.applicableType}</span>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Sleeping Room Availability */}
                  {proposalData.proposal.sleepingRoomAvailability && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Sleeping Room Availability</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Requirements Met:</span>
                            {proposalData.proposal.sleepingRoomAvailability.sleepingRoomReqMetTypeId ? (
                              <span className="flex items-center gap-1 text-success-600 font-medium">
                                <CheckCircle className="w-4 h-4" />
                                Yes
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-muted-foreground font-medium">
                                <XCircle className="w-4 h-4" />
                                No
                              </span>
                            )}
                            {proposalData.proposal.sleepingRoomAvailability.sleepingRoomReqMetType && (
                              <span className="text-muted-foreground">({proposalData.proposal.sleepingRoomAvailability.sleepingRoomReqMetType})</span>
                            )}
                          </div>
                          {proposalData.proposal.sleepingRoomAvailability.additionalInfo && (
                            <div>
                              <span className="text-muted-foreground">Additional Information:</span>
                              <p className="mt-1 text-foreground whitespace-pre-wrap">{proposalData.proposal.sleepingRoomAvailability.additionalInfo}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Meeting Space Availability */}
                  {proposalData.proposal.meetingSpaceAvailability && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Meeting Space Availability</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Requirements Met:</span>
                            {proposalData.proposal.meetingSpaceAvailability.meetingRoomReqMetTypeId ? (
                              <span className="flex items-center gap-1 text-success-600 font-medium">
                                <CheckCircle className="w-4 h-4" />
                                Yes
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-muted-foreground font-medium">
                                <XCircle className="w-4 h-4" />
                                No
                              </span>
                            )}
                          </div>
                          {proposalData.proposal.meetingSpaceAvailability.avNeeds && (
                            <div>
                              <span className="text-muted-foreground">AV Needs:</span>
                              <p className="mt-1 text-foreground">{proposalData.proposal.meetingSpaceAvailability.avNeeds}</p>
                            </div>
                          )}
                          {proposalData.proposal.meetingSpaceAvailability.additionalInfo && (
                            <div>
                              <span className="text-muted-foreground">Additional Information:</span>
                              <p className="mt-1 text-foreground whitespace-pre-wrap">{proposalData.proposal.meetingSpaceAvailability.additionalInfo}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Event Availability */}
                  {proposalData.proposal.eventAvailability && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Event Availability</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3 text-sm">
                          {proposalData.proposal.eventAvailability.startDate && (
                            <div>
                              <span className="text-muted-foreground">Start Date:</span>
                              <span className="ml-2 font-medium">{formatDate(proposalData.proposal.eventAvailability.startDate)}</span>
                            </div>
                          )}
                          {proposalData.proposal.eventAvailability.endDate && (
                            <div>
                              <span className="text-muted-foreground">End Date:</span>
                              <span className="ml-2 font-medium">{formatDate(proposalData.proposal.eventAvailability.endDate)}</span>
                            </div>
                          )}
                          {proposalData.proposal.eventAvailability.availability && (
                            <div>
                              <span className="text-muted-foreground">Availability:</span>
                              <span className="ml-2 font-medium">{proposalData.proposal.eventAvailability.availability}</span>
                            </div>
                          )}
                          {proposalData.proposal.eventAvailability.hold !== undefined && (
                            <div>
                              <span className="text-muted-foreground">Hold:</span>
                              <span className="ml-2 font-medium">
                                {proposalData.proposal.eventAvailability.hold ? (
                                  <span className="flex items-center gap-1 text-success-600">
                                    <CheckCircle className="w-4 h-4" />
                                    Yes
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 text-muted-foreground">
                                    <XCircle className="w-4 h-4" />
                                    No
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          {proposalData.proposal.eventAvailability.additionalInfo && (
                            <div>
                              <span className="text-muted-foreground">Additional Information:</span>
                              <p className="mt-1 text-foreground whitespace-pre-wrap">{proposalData.proposal.eventAvailability.additionalInfo}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Cvent Proposal Summary */}
                  {proposalSummary && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Cvent Summary</CardTitle>
                        <CardDescription>
                          Breakdown of proposal costs and details
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-strong:font-semibold prose-ul:text-foreground prose-li:text-foreground">
                          <ReactMarkdown
                            components={{
                              h1: ({...props}: any) => <h1 className="text-xl font-semibold mb-3 mt-4 first:mt-0" {...props} />,
                              h2: ({...props}: any) => <h2 className="text-lg font-semibold mb-2 mt-3 first:mt-0" {...props} />,
                              h3: ({...props}: any) => <h3 className="text-base font-semibold mb-2 mt-2 first:mt-0" {...props} />,
                              p: ({...props}: any) => <p className="mb-2 text-sm leading-relaxed" {...props} />,
                              ul: ({...props}: any) => <ul className="list-disc list-inside mb-2 space-y-1 ml-2" {...props} />,
                              ol: ({...props}: any) => <ol className="list-decimal list-inside mb-2 space-y-1 ml-2" {...props} />,
                              li: ({...props}: any) => <li className="text-sm" {...props} />,
                              strong: ({...props}: any) => <strong className="font-semibold" {...props} />,
                              em: ({...props}: any) => <em className="italic" {...props} />,
                            }}
                          >
                            {proposalSummary?.replace(/\n(?!\n)/g, '  \n')}
                          </ReactMarkdown>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Tax Additional Info */}
                  {proposalData.proposal.taxAdditionalInfo && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Tax Additional Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {proposalData.proposal.taxAdditionalInfo}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Event Additional Info */}
                  {proposalData.proposal.eventAdditionalInfo && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Event Additional Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {proposalData.proposal.eventAdditionalInfo}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Estimated Costs Additional Info */}
                  {proposalData.proposal.estimatedCostsAdditionalInfo && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Estimated Costs Additional Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {proposalData.proposal.estimatedCostsAdditionalInfo}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Union Info */}
                  {proposalData.proposal.unionInfo && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Union Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {proposalData.proposal.unionInfo}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Contractor Info */}
                  {proposalData.proposal.contractorInfo && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Contractor Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {proposalData.proposal.contractorInfo}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Contact Information */}
                  {proposalData.contact && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Contact Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {proposalData.contact.firstName || proposalData.contact.lastName ? (
                            <div>
                              <span className="text-muted-foreground">Name:</span>
                              <span className="ml-2 font-medium">
                                {[proposalData.contact.firstName, proposalData.contact.lastName].filter(Boolean).join(' ') || '-'}
                              </span>
                            </div>
                          ) : null}
                          {proposalData.contact.title && (
                            <div>
                              <span className="text-muted-foreground">Title:</span>
                              <span className="ml-2 font-medium">{proposalData.contact.title}</span>
                            </div>
                          )}
                          {proposalData.contact.organization && (
                            <div>
                              <span className="text-muted-foreground">Organization:</span>
                              <span className="ml-2 font-medium">{proposalData.contact.organization}</span>
                            </div>
                          )}
                          {proposalData.contact.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Email:</span>
                              <a href={`mailto:${proposalData.contact.email}`} className="ml-2 font-medium text-primary-600 hover:underline">
                                {proposalData.contact.email}
                              </a>
                            </div>
                          )}
                          {proposalData.contact.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Phone:</span>
                              <span className="ml-2 font-medium">{proposalData.contact.phone}</span>
                            </div>
                          )}
                          {proposalData.contact.mobile && (
                            <div>
                              <span className="text-muted-foreground">Mobile:</span>
                              <span className="ml-2 font-medium">{proposalData.contact.mobile}</span>
                            </div>
                          )}
                          {proposalData.contact.fax && (
                            <div>
                              <span className="text-muted-foreground">Fax:</span>
                              <span className="ml-2 font-medium">{proposalData.contact.fax}</span>
                            </div>
                          )}
                          {proposalData.contact.organizationWebsite && (
                            <div>
                              <span className="text-muted-foreground">Website:</span>
                              <a href={proposalData.contact.organizationWebsite} target="_blank" rel="noopener noreferrer" className="ml-2 font-medium text-primary-600 hover:underline">
                                {proposalData.contact.organizationWebsite}
                              </a>
                            </div>
                          )}
                          {proposalData.contact.additionalInfo && (
                            <div className="md:col-span-2">
                              <span className="text-muted-foreground">Additional Info:</span>
                              <p className="mt-1 text-foreground whitespace-pre-wrap">{proposalData.contact.additionalInfo}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Venue Information */}
                  {(proposalData.name || proposalData.address1 || proposalData.venueType) && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Venue Information</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {proposalData.name && (
                            <div>
                              <span className="text-muted-foreground">Name:</span>
                              <span className="ml-2 font-medium">{proposalData.name}</span>
                            </div>
                          )}
                          {proposalData.venueType && (
                            <div>
                              <span className="text-muted-foreground">Type:</span>
                              <span className="ml-2 font-medium">{proposalData.venueType}</span>
                            </div>
                          )}
                          {(proposalData.address1 || proposalData.address2) && (
                            <div className="flex items-start gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                              <div>
                                <span className="text-muted-foreground">Address:</span>
                                <p className="mt-1 font-medium">
                                  {[proposalData.address1, proposalData.address2, proposalData.stateProvinceCode, proposalData.postalCode, proposalData.countryCode]
                                    .filter(Boolean)
                                    .join(', ')}
                                </p>
                              </div>
                            </div>
                          )}
                          {proposalData.venueMainPhone && (
                            <div>
                              <span className="text-muted-foreground">Main Phone:</span>
                              <span className="ml-2 font-medium">{proposalData.venueMainPhone}</span>
                            </div>
                          )}
                          {proposalData.venueSalesPhone && (
                            <div>
                              <span className="text-muted-foreground">Sales Phone:</span>
                              <span className="ml-2 font-medium">{proposalData.venueSalesPhone}</span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* View Raw Data Button - Always visible */}
                  <Card>
                    <CardContent className="pt-6">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleViewRawData}
                        leftIcon={<FileText className="w-4 h-4" />}
                      >
                        View Raw Database Data
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        View all Cvent-related fields stored in the database for this venue
                      </p>
                    </CardContent>
                  </Card>

                  {/* Notes & Recommendations */}
                  {proposalData.notesRecommendations && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Notes & Recommendations</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-foreground whitespace-pre-wrap">
                          {proposalData.notesRecommendations}
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <div className="py-8 text-center text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No proposal data available</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {!loading && !error && !proposalData && (
          <div className="py-8 text-center text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No proposal data available</p>
          </div>
        )}
      </VStack>

      {/* File Viewer Modal */}
      <FileViewerModal
        isOpen={!!selectedFile}
        onClose={() => setSelectedFile(null)}
        file={selectedFile}
      />

      {/* Raw Database Data Modal */}
      <Modal
        isOpen={showRawDataModal}
        onClose={() => {
          setShowRawDataModal(false);
          setRawDatabaseData(null);
        }}
        title="Raw Database Data"
        size="lg"
      >
        <VStack gap={4}>
          {loadingRawData ? (
            <LoadingState text="Loading database data..." />
          ) : rawDatabaseData ? (
            <>
              <p className="text-sm text-muted-foreground">
                This shows all Cvent-related fields stored in the database for this venue.
              </p>
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium border-b">Database Field</th>
                        <th className="px-4 py-2 text-left font-medium border-b">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(rawDatabaseData).map(([field, value]) => (
                        <tr key={field} className="border-b hover:bg-muted/50">
                          <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                            {field}
                          </td>
                          <td className="px-4 py-2 break-words">
                            {value === null || value === undefined ? (
                              <span className="text-muted-foreground italic">null</span>
                            ) : typeof value === 'object' ? (
                              <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-w-md">
                                {JSON.stringify(value, null, 2)}
                              </pre>
                            ) : typeof value === 'boolean' ? (
                              <span className="font-mono">{String(value)}</span>
                            ) : (
                              <span>{String(value)}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {Object.keys(rawDatabaseData).length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No Cvent data stored in database yet.</p>
                  <p className="text-xs mt-2">Data will be stored when you open this modal or refresh proposals.</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No data available</p>
            </div>
          )}
        </VStack>
      </Modal>
    </Modal>
  );
}
